// صفحهٔ «بکاپ» — فقط ساختن و نگه‌داری. بازیابی عمداً اینجا نیست (تبِ «بازیابی»).
// دو نوعِ کاملاً جدا: «بکاپ دیتابیس» و «بکاپ فایل‌ها و ضمیمه‌ها».
import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Descriptions, Input, InputNumber, Modal, Row, Select, Space, Switch, Tabs, Tag, Typography, Upload, message } from "antd";
import Alert from "../../components/GxAlert";
import {
  CloudUploadOutlined, DatabaseOutlined, FileZipOutlined, ReloadOutlined,
  SaveOutlined, UploadOutlined,
} from "@ant-design/icons";
import {
  deleteBackup, downloadBackup, getBackupConfig, listBackups, previewBackup,
  reindexBackups, runDbBackup, runFilesBackup, saveBackupConfig, uploadBackup,
  verifyBackup,
} from "../../api/client";
import DismissibleHelp from "../../components/DismissibleHelp";
import BackupHistoryTable, { humanSize } from "./backup/BackupHistoryTable";

const { Text, Title } = Typography;
const WEEKDAYS = ["شنبه", "یک‌شنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنج‌شنبه", "جمعه"];

const RETENTION_MODES = [
  { value: "count", label: "تعداد نسخه" },
  { value: "days", label: "تعداد روز" },
  { value: "never", label: "هیچ‌وقت حذف نکن" },
];

/** کارتِ Retention یک نوعِ بکاپ — قاعده صریح است تا چیزی بی‌سروصدا پاک نشود. */
function RetentionCard({ cfg, set, prefix, label }) {
  const mode = cfg[`${prefix}_retention_mode`];
  return (
    <Card size="small" title={`نگه‌داری نسخه‌های ${label}`} style={{ marginTop: 12 }}>
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12}>
          <Text type="secondary" style={{ fontSize: 12 }}>قاعدهٔ حذف</Text>
          <Select style={{ width: "100%" }} value={mode} options={RETENTION_MODES}
            onChange={(v) => set(`${prefix}_retention_mode`, v)} />
        </Col>
        {mode === "count" && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 12 }}>چند نسخهٔ آخر بماند</Text>
            <InputNumber min={1} max={365} style={{ width: "100%" }}
              value={cfg[`${prefix}_retention_count`]}
              onChange={(v) => set(`${prefix}_retention_count`, v)} />
          </Col>
        )}
        {mode === "days" && (
          <Col xs={24} sm={12}>
            <Text type="secondary" style={{ fontSize: 12 }}>نسخه‌های قدیمی‌تر از چند روز</Text>
            <InputNumber min={1} max={3650} style={{ width: "100%" }}
              value={cfg[`${prefix}_retention_days`]}
              onChange={(v) => set(`${prefix}_retention_days`, v)} />
          </Col>
        )}
      </Row>
      <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
        {mode === "never"
          ? "هیچ نسخه‌ای خودکار حذف نمی‌شود — حذف فقط دستی."
          : "بکاپ‌های ایمنی (پیش از بروزرسانی/بازیابی) و آپلودی هیچ‌وقت خودکار حذف نمی‌شوند."}
      </Text>
    </Card>
  );
}

/** یک نوعِ بکاپ: دکمهٔ اجرا + Retention + آپلود + تاریخچه. */
function BackupKindPanel({ kind, label, icon, cfg, set, onRun, running, help, accept }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listBackups(kind)
      .then((r) => setRows(r.data))
      .catch(() => message.error("خطا در بارگذاریِ تاریخچه"))
      .finally(() => setLoading(false));
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    const ok = await onRun();
    if (ok) load();
  };
  const del = async (id) => {
    try { await deleteBackup(id); message.success("حذف شد"); load(); }
    catch { message.error("خطا در حذف"); }
  };
  const dl = async (r) => {
    try { await downloadBackup(r.id, r.filename); }
    catch { message.error("خطا در دانلود"); }
  };
  const verify = async (r) => {
    try {
      const { data } = await verifyBackup(r.id);
      Modal[data.ok ? "success" : "error"]({
        title: data.ok ? "فایلِ بکاپ سالم است" : "فایلِ بکاپ مشکل دارد",
        content: (
          <div style={{ direction: "rtl" }}>
            <p>{data.message || (data.ok ? "چک‌سام و ساختارِ فایل تأیید شد." : "")}</p>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {data.checksum_known
                ? "چک‌سام با مقدارِ ثبت‌شده مقایسه شد."
                : "این بکاپ چک‌سامِ ثبت‌شده ندارد (نسخهٔ قدیمی)."}
            </Text>
          </div>
        ),
      });
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بررسی"); }
  };
  const showPreview = async (r) => {
    try { const { data } = await previewBackup(r.id); setPreview({ rec: r, data }); }
    catch { message.error("خطا در پیش‌نمایش"); }
  };
  const onUpload = async (file) => {
    try {
      const { data } = await uploadBackup(file, kind);
      if (data.validation?.ok) message.success("فایل آپلود و تأیید شد");
      else message.warning("فایل ذخیره شد ولی معتبر نیست: " + (data.validation?.message || ""));
      load();
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در آپلود"); }
    return false;   // جلوگیری از آپلودِ خودکارِ AntD
  };

  const enabledKey = kind === "database" ? "db_backup_enabled" : "files_backup_enabled";
  const prefix = kind === "database" ? "db" : "files";
  const nextRun = kind === "database" ? cfg.next_run_database : cfg.next_run_files;

  return (
    <div style={{ direction: "rtl" }}>
      <DismissibleHelp id={`backup-${kind}`} message={help} />
      <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
        <Col xs={24} lg={9}>
          <Card size="small" title={<Space>{icon}{label}</Space>}
            extra={<Space><Text type="secondary" style={{ fontSize: 12 }}>خودکار</Text>
              <Switch size="small" checked={cfg[enabledKey]}
                onChange={(v) => set(enabledKey, v)} /></Space>}>
            <Button type="primary" icon={<CloudUploadOutlined />} loading={running}
              onClick={run} block>
              ساختِ بکاپ الان
            </Button>
            <div style={{ marginTop: 10, fontSize: 12 }}>
              <Text type="secondary">اجرای خودکارِ بعدی: </Text>
              {cfg[enabledKey] && nextRun
                ? <Tag color="blue">{nextRun.slice(0, 16).replace("T", " ")}</Tag>
                : <Text type="secondary">—</Text>}
            </div>
            {kind === "files" && cfg.uploads && (
              <div style={{ marginTop: 10, fontSize: 12 }}>
                <Text type="secondary">فایل‌های آپلودیِ ذخیره‌شده: </Text>
                <Tag>{cfg.uploads.file_count} فایل · {humanSize(cfg.uploads.total_bytes)}</Tag>
              </div>
            )}
          </Card>

          <RetentionCard cfg={cfg} set={set} prefix={prefix} label={label} />

          <Card size="small" title="آپلودِ فایلِ بکاپِ موجود" style={{ marginTop: 12 }}>
            <Upload beforeUpload={onUpload} showUploadList={false} accept={accept}>
              <Button icon={<UploadOutlined />} block>انتخابِ فایل</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
              فایل بلافاصله بعد از آپلود از نظرِ نوع و ساختار بررسی می‌شود.
            </Text>
          </Card>
        </Col>

        <Col xs={24} lg={15}>
          <Card size="small" title={`تاریخچهٔ ${label}`}
            extra={<Space>
              <Button size="small" onClick={async () => {
                try {
                  const { data } = await reindexBackups();
                  message.success(data.added
                    ? `${data.added} فایلِ بدونِ ردیف ثبت شد` : "همه‌چیز هم‌تراز است");
                  load();
                } catch { message.error("خطا"); }
              }}>هم‌ترازی با دیسک</Button>
              <Button size="small" icon={<ReloadOutlined />} onClick={load}>تازه‌سازی</Button>
            </Space>}>
            <BackupHistoryTable rows={rows} loading={loading} onDownload={dl} onDelete={del}
              onVerify={kind === "files" ? showPreview : verify} />
          </Card>
        </Col>
      </Row>

      <Modal open={!!preview} onCancel={() => setPreview(null)} footer={null} width={720}
        title={`محتوای ${preview?.rec?.filename || ""}`} destroyOnHidden>
        {preview?.data && (
          <div style={{ direction: "rtl" }}>
            <Descriptions size="small" column={2} bordered>
              <Descriptions.Item label="تعداد فایل">{preview.data.file_count}</Descriptions.Item>
              <Descriptions.Item label="حجمِ محتوا">{humanSize(preview.data.total_bytes)}</Descriptions.Item>
              <Descriptions.Item label="نسخهٔ برنامه">{preview.data.app_version || "—"}</Descriptions.Item>
              <Descriptions.Item label="زمانِ ساخت">
                {(preview.data.built_at || "").slice(0, 16).replace("T", " ") || "—"}
              </Descriptions.Item>
            </Descriptions>
            <Title level={5} style={{ marginTop: 12 }}>بخش‌ها</Title>
            <Space wrap>
              {Object.entries(preview.data.by_root || {}).map(([k, v]) => (
                <Tag key={k} color="blue">{k}: {v.files} فایل · {humanSize(v.bytes)}</Tag>
              ))}
            </Space>
            <Title level={5} style={{ marginTop: 12 }}>نمونهٔ فایل‌ها</Title>
            <div style={{
              maxHeight: 220, overflow: "auto", fontSize: 12,
              background: "rgba(0,0,0,.03)", padding: 8,
            }}>
              {(preview.data.sample || []).map((n) => <div key={n}>{n}</div>)}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function BackupTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [runningDb, setRunningDb] = useState(false);
  const [runningFiles, setRunningFiles] = useState(false);

  const loadCfg = () => getBackupConfig().then((r) => setCfg(r.data))
    .catch(() => message.error("خطا در بارگذاریِ تنظیمات"));
  useEffect(() => { loadCfg(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await saveBackupConfig(cfg);
      setCfg(r.data);
      message.success("تنظیمات ذخیره شد");
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در ذخیره"); }
    setSaving(false);
  };

  const runDb = async () => {
    setRunningDb(true);
    try { await runDbBackup(); message.success("بکاپ دیتابیس ساخته شد"); return true; }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در ساختِ بکاپ"); return false; }
    finally { setRunningDb(false); }
  };
  const runFiles = async () => {
    setRunningFiles(true);
    try { await runFilesBackup(); message.success("بکاپ فایل‌ها و ضمیمه‌ها ساخته شد"); return true; }
    catch (e) { message.error(e?.response?.data?.detail || "خطا در ساختِ بکاپ"); return false; }
    finally { setRunningFiles(false); }
  };

  if (!cfg) return <Card loading />;

  const items = [
    {
      key: "database",
      label: <Space><DatabaseOutlined />بکاپ دیتابیس</Space>,
      children: (
        <BackupKindPanel
          kind="database" label="بکاپ دیتابیس" icon={<DatabaseOutlined />}
          cfg={cfg} set={set} onRun={runDb} running={runningDb} accept=".dump,.sql,.backup,.enc"
          help="همهٔ جدول‌ها و اطلاعاتِ ساختاری: پرسنل، تولید، حقوق، حضور و غیاب، فاکتورها، زنجیرهٔ تأمین، تنظیمات، تاریخچه‌ها و کاربران. خروجی فایلِ استانداردِ Postgres است و مستقیماً قابلِ بازیابی. توکن‌ها و رمزهای تنظیماتِ بکاپ عمداً داخلِ فایل نمی‌روند." />
      ),
    },
    {
      key: "files",
      label: <Space><FileZipOutlined />بکاپ فایل‌ها و ضمیمه‌ها</Space>,
      children: (
        <BackupKindPanel
          kind="files" label="بکاپ فایل‌ها و ضمیمه‌ها" icon={<FileZipOutlined />}
          cfg={cfg} set={set} onRun={runFiles} running={runningFiles} accept=".tar.gz,.tgz,.gz,.enc"
          help="فایل‌هایی که داخلِ دیتابیس نیستند: فایل‌های آپلودیِ ایمپورت (اکسل، PDF، DAT)، سورسِ برنامه و تنظیماتِ ساختاری. مقدارِ رمزها و توکن‌ها از فایلِ تنظیمات پاک می‌شود و فقط نامِ کلیدها می‌ماند." />
      ),
    },
  ];

  return (
    <div style={{ direction: "rtl" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Card size="small" title="زمان‌بندیِ خودکار و کانال‌های ارسال"
            extra={<Space>
              <Text type="secondary">فعال</Text>
              <Switch checked={cfg.enabled} onChange={(v) => set("enabled", v)} />
              <Button type="primary" size="small" icon={<SaveOutlined />} loading={saving} onClick={save}>
                ذخیرهٔ تنظیمات
              </Button>
            </Space>}>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8} md={5}>
                <Text type="secondary" style={{ fontSize: 12 }}>دوره</Text>
                <Select style={{ width: "100%" }} value={cfg.frequency}
                  onChange={(v) => set("frequency", v)}
                  options={[{ value: "daily", label: "روزانه" }, { value: "weekly", label: "هفتگی" }]} />
              </Col>
              {cfg.frequency === "weekly" && (
                <Col xs={24} sm={8} md={5}>
                  <Text type="secondary" style={{ fontSize: 12 }}>روزِ هفته</Text>
                  <Select style={{ width: "100%" }} value={cfg.weekday ?? 0}
                    onChange={(v) => set("weekday", v)}
                    options={WEEKDAYS.map((d, i) => ({ value: i, label: d }))} />
                </Col>
              )}
              <Col xs={24} sm={8} md={4}>
                <Text type="secondary" style={{ fontSize: 12 }}>ساعت (HH:MM)</Text>
                <Input value={cfg.time_hhmm} onChange={(e) => set("time_hhmm", e.target.value)}
                  placeholder="02:00" maxLength={5} />
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Text type="secondary" style={{ fontSize: 12 }}>سقفِ هر فایلِ تلگرام (MB)</Text>
                <InputNumber min={1} max={50} style={{ width: "100%" }} value={cfg.tg_split_mb}
                  onChange={(v) => set("tg_split_mb", v)} />
              </Col>
              <Col xs={24} sm={12} md={5}>
                <Text type="secondary" style={{ fontSize: 12 }}>رمزنگاریِ فایلِ بکاپ</Text>
                <div>
                  <Switch checked={cfg.encrypt_backups} disabled={!cfg.encryption_available}
                    onChange={(v) => set("encrypt_backups", v)} />
                  <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>
                    {cfg.encryption_available
                      ? `کلید: ${cfg.encryption_fingerprint}`
                      : "کلیدِ BACKUP_ENCRYPTION_KEY تنظیم نشده"}
                  </Text>
                </div>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col xs={24} lg={12}>
                <Card size="small" title="ارسالِ ایمیل (SMTP)"
                  extra={<Switch size="small" checked={cfg.email_enabled}
                    onChange={(v) => set("email_enabled", v)} />}>
                  <Row gutter={[8, 8]}>
                    <Col xs={24} sm={16}><Input addonBefore="سرور" value={cfg.smtp_host}
                      onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com" /></Col>
                    <Col xs={24} sm={8}><InputNumber addonBefore="پورت" style={{ width: "100%" }}
                      value={cfg.smtp_port} onChange={(v) => set("smtp_port", v)} /></Col>
                    <Col xs={24} sm={12}><Input addonBefore="کاربر" value={cfg.smtp_user}
                      onChange={(e) => set("smtp_user", e.target.value)} /></Col>
                    <Col xs={24} sm={12}><Input.Password addonBefore="رمز"
                      placeholder={cfg.has_smtp_pass
                        ? `•••• (از ${cfg.smtp_pass_source === "env" ? "env" : "دیتابیس"})` : ""}
                      onChange={(e) => set("smtp_pass", e.target.value)} /></Col>
                    <Col xs={24} sm={12}><Input addonBefore="فرستنده" value={cfg.smtp_from}
                      onChange={(e) => set("smtp_from", e.target.value)} /></Col>
                    <Col xs={24} sm={12}><Input addonBefore="گیرنده" value={cfg.smtp_to}
                      onChange={(e) => set("smtp_to", e.target.value)} placeholder="a@x.com, b@y.com" /></Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card size="small" title="ارسالِ تلگرام (بات)"
                  extra={<Switch size="small" checked={cfg.telegram_enabled}
                    onChange={(v) => set("telegram_enabled", v)} />}>
                  <Row gutter={[8, 8]}>
                    <Col xs={24}><Input.Password addonBefore="Bot Token"
                      placeholder={cfg.has_tg_token
                        ? `•••• (از ${cfg.tg_token_source === "env" ? "env" : "دیتابیس"})` : "123:ABC"}
                      onChange={(e) => set("tg_bot_token", e.target.value)} /></Col>
                    <Col xs={24}><Input addonBefore="Chat ID" value={cfg.tg_chat_id}
                      onChange={(e) => set("tg_chat_id", e.target.value)} placeholder="-100..." /></Col>
                  </Row>
                  <Alert style={{ marginTop: 10 }} type="info" showIcon
                    message="بکاپ روزانه به‌صورتِ دو فایلِ جدا ارسال می‌شود؛ فایلِ بزرگ‌تر از سقف، فشرده یا تکه‌تکه می‌شود. توکن را می‌توان به‌جای دیتابیس در متغیرِ محیطیِ BACKUP_TG_BOT_TOKEN گذاشت." />
                </Card>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24}>
          <Tabs items={items} />
        </Col>
      </Row>
    </div>
  );
}
